/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { Fragment, useEffect, useState } from 'react';
import { Transition, Dialog } from '@headlessui/react';
import { Order } from '@Types/cart/Order';
import { useDarkMode } from 'frontastic';
import { fetchApiHub } from 'frontastic/lib/fetch-api-hub';
import { DocumentRemoveIcon, XCircleIcon } from '@heroicons/react/solid';
import { useAccount } from 'frontastic';
import { BusinessUnit } from '@Types/business-unit/BusinessUnit';
import { v4 as uuidv4 } from 'uuid';

interface Props {
  open: boolean;
  onClose: () => void;
  order: Order;
}

const UploadsDocumentsModal: React.FC<Props> = ({ open, onClose, order }) => {
    const { mode } = useDarkMode();
    const [type, setType] = useState('invoices');
    const [canManageDocuments, setCanManageDocuments] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [file, setFile] = useState<null|File>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [refresh, setRefresh] = useState(false);
    const { account } = useAccount();

    useEffect(() => {
      fetchApiHub('/action/bic-order-download-document/queryCustomObject', {method: 'POST'}, {where: `value(order="${order.orderId}")`}).then((response) => {
        setDocuments(response);
      });
      fetchApiHub(`/action/business-unit/getByKey?key=${order.businessUnit}`).then((response: BusinessUnit) => {
          const associate = response.associates.filter((associate) => {
            return associate.customer.id === account.accountId;
          });
          if (associate.length === 1) {
            const buyerRole = associate.at(0).associateRoleAssignments.filter((role) => {
              return role.associateRole.key.toLowerCase() === "buyer";
            });
            setCanManageDocuments(buyerRole.length === 0);
          }
      })
    }, [refresh]);


    function getDocumentUrl(document: any) {
      fetchApiHub('/action/bic-order-download-document/getSignedUrlForGet', 
        {method: 'POST'}, 
        {awsKey: document.value.awsKey, bucket: document.value.bucket, filename: document.value.filename}
      ).then((response) => {
        fetch(response.url, {method: 'GET'}).then((response) => {
          window.open(response.url, '_blank');
        })
      });
    }

    function removeDocument(document: any) {
      fetchApiHub('/action/bic-order-download-document/deleteCustomObject', {method: 'POST'}, {awsKey: document.value.awsKey, bucket: document.value.bucket}).then((response) => {
        setRefresh(!refresh);
      })
    }
    
    function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
        setFile(e.target.files.item(0));
    }
    function handleTypeChange(e: React.ChangeEvent<HTMLSelectElement>) {
        setType(e.target.value);
    }

    const handleSubmit = async (e: React.FormEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (true === isLoading) {
        return;
      }
      setIsLoading(true);
      const objectKey = self.crypto.randomUUID();
      fetchApiHub(
        '/action/bic-order-download-document/getSignedUrlForPut', 
        {method: 'POST'}, 
        {
          awsKey: objectKey,
          filename: file.name
        }
      ).then((signedUrl) => {
        fetch(signedUrl.url, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type
          },
          body: file
        }).then((awsResponse) => {
          if (awsResponse.status < 300) {
            fetchApiHub(
              '/action/bic-order-download-document/putCustomObject', 
              {method: 'POST'}, 
              {
                order: order.orderId, 
                type: type, 
                awsKey: objectKey,
                filename: file.name,
                region: signedUrl.region,
                bucket: signedUrl.bucket
              }
            ).then((response) => {
              setRefresh(!refresh);
              setIsLoading(false);
            })
          }
        });
      });
    }

    if (!order) {
      return null;
    }
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog className={`${mode} default fixed inset-0 z-10 overflow-y-auto`} onClose={onClose}>
        <Transition.Root>
          <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-left sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 opacity-75 transition-opacity" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="absolute inset-0" onClick={onClose}>
                {/* eslint-disable */}
                <div
                  className="absolute top-1/2 left-1/2 h-[90vh] w-[60%] max-w-[1200px] -translate-x-1/2 -translate-y-1/2 overflow-auto bg-white py-16 px-4 dark:bg-primary-200 sm:px-6 lg:py-24 lg:px-8"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h2 className="mb-8 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-light-100 sm:text-4xl text-center">
                      Documents for order {order.orderId}
                  </h2>
                  <div className='table-order-documents-container'>
                    <table className='w-full table-fixed border border-gray-300'>
                      <thead className='font-bold'>
                        <tr>
                          <th className='w-1/4 text-center border border-gray-300'>Invoices</th>
                          <th className='w-1/4 text-center border border-gray-300'>Packing list</th>
                          <th className='w-1/4 text-center border border-gray-300'>BOL</th>
                          <th className='w-1/4 text-center border border-gray-300'>Other</th>
                        </tr>
                      </thead>
                      <tbody className='font-color-bic'>
                        <tr>
                          <td className='align-top p-2 bg-gray-100 border border-gray-300'>
                            {!!documents?.length && documents.map((document) => document.value.type === "invoices" && (
                              <div key={uuidv4()} className="space-y-1">
                                <div key={uuidv4()} className='flex items-start gap-2'>
                                  <span onClick={() => getDocumentUrl(document)} className='overflow-hidden text-ellipsis text-sm cursor-pointer'>
                                    {document.value.filename}
                                  </span>
                                  <XCircleIcon className="h-4 w-4 cursor-pointer shrink-0" onClick={() => removeDocument(document)}></XCircleIcon>
                                </div>
                              </div>
                            ))}
                          </td>
                          <td className='align-top p-2 bg-gray-100 border border-gray-300'>
                            {!!documents?.length && documents.map((document) => document.value.type === "packing_list" && (
                              <div className="space-y-1">
                                <div key={uuidv4()} className='flex items-start gap-1'>
                                  <span onClick={() => getDocumentUrl(document)} className='overflow-hidden text-ellipsis text-sm cursor-pointer'>{document.value.filename}</span>
                                  <XCircleIcon className="h-4 w-4 cursor-pointer shrink-0" onClick={() => removeDocument(document)}></XCircleIcon>
                                </div>
                              </div>
                            ))}
                          </td>
                          <td className='align-top p-2 bg-gray-100 border border-gray-300'>
                            {!!documents?.length && documents.map((document) => document.value.type === "bol" && (
                              <div className="space-y-1">
                                <div key={uuidv4()} className='flex items-start gap-1'>
                                  <span onClick={() => getDocumentUrl(document)} className='overflow-hidden text-ellipsis text-sm cursor-pointer'>{document.value.filename}</span>
                                  <XCircleIcon className="h-4 w-4 cursor-pointer shrink-0" onClick={() => removeDocument(document)}></XCircleIcon>
                                </div>
                              </div>
                            ))}
                          </td>
                          <td className='align-top p-2 bg-gray-100 border border-gray-300'>
                            {!!documents?.length && documents.map((document) => document.value.type === "other" && (
                              <div className="space-y-1">
                                <div key={uuidv4()} className='flex items-start gap-1'>
                                  <span onClick={() => getDocumentUrl(document)} className='overflow-hidden text-ellipsis text-sm cursor-pointer'>{document.value.filename}</span>
                                  <XCircleIcon className="h-4 w-4 cursor-pointer shrink-0" onClick={() => removeDocument(document)}></XCircleIcon>
                                </div>
                              </div>
                            ))}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  {canManageDocuments && (
                    <div className="relative max-w-xs float-left pt-4">
                        <h3>Upload document</h3>
                        <span>
                          <form className='' method='POST' onSubmit={handleSubmit} action=''>
                              <div className='pt-4'>
                                <select name='document_name' onChange={handleTypeChange} className='w-full'>
                                    <option value="invoices">Invoices</option>
                                    <option value="packing_list">Packing List</option>
                                    <option value="bol">BOL</option>
                                    <option value="other">Other</option>
                                </select>
                              </div>
                              <div className='pt-2'>
                                <input type='file' name='files' multiple={false} onChange={handleFileChange} className='w-full' />
                              </div>
                              {file && (<div className='pt-1'>
                                <button 
                                  type="button" 
                                  onClick={handleSubmit}
                                  className="btn-checkout btn-primary mt-5 w-full loader"
                                  disabled={isLoading}
                                >Upload document</button>
                              </div>)}
                          </form>
                        </span>
                    </div>
                  )}
                </div>
              </div>
            </Transition.Child>
          </div>
        </Transition.Root>
      </Dialog>
    </Transition.Root>
  );
};

export default UploadsDocumentsModal;
